// Professional avatar system with initials support

export interface AvatarOption {
  id: string;
  name: string;
  url: string;
  description: string;
}

// Get initials from handle (max 2 characters)
export const getInitialsFromHandle = (handle: string): string => {
  if (!handle) return 'US'; // Default User
  
  // Remove special characters and numbers
  const cleanHandle = handle.replace(/[^a-zA-Z\s]/g, '').trim();
  
  // Split into words
  const words = cleanHandle.split(/\s+/).filter(word => word.length > 0);
  
  if (words.length === 0) {
    // Fallback: use first 2 letters of original handle
    return handle.substring(0, 2).toUpperCase();
  }
  
  if (words.length === 1) {
    // Single word: use first 2 letters
    return words[0].substring(0, 2).toUpperCase();
  }
  
  // Multiple words: use first letter of first 2 words
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Generate initials avatar URL (SVG data URI)
export const getInitialsAvatarUrl = (handle: string): string => {
  const initials = getInitialsFromHandle(handle);
  
  // Generate consistent color from handle
  let hash = 0;
  for (let i = 0; i < handle.length; i++) {
    hash = handle.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate professional color palette
  const hue = Math.abs(hash % 360);
  const bgColor = `hsl(${hue}, 45%, 35%)`; // Dark professional color
  const textColor = '#FFFFFF'; // White text
  
  // Create SVG
  const svg = `
    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="100" fill="${bgColor}" rx="0"/>
      <text 
        x="50" 
        y="50" 
        font-family="system-ui, -apple-system, sans-serif" 
        font-size="40" 
        font-weight="600"
        fill="${textColor}" 
        text-anchor="middle" 
        dominant-baseline="central"
      >${initials}</text>
    </svg>
  `.trim();
  
  // Convert to data URI
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Get avatar URL - supports both pixel art and initials
export const getAvatarUrl = (
  seedOrId: string, 
  useInitials: boolean = false,
  handle?: string
): string => {
  if (useInitials && handle) {
    return getInitialsAvatarUrl(handle);
  }
  
  // Default: Pixel art avatar
  return `https://api.dicebear.com/7.x/pixel-art/svg?seed=${seedOrId}`;
};

// Get avatar option by seed or ID
export const getAvatarOption = (
  seedOrId: string,
  useInitials: boolean = false,
  handle?: string
): AvatarOption => {
  if (useInitials && handle) {
    return {
      id: `initials-${handle}`,
      name: 'Professional Initials',
      url: getInitialsAvatarUrl(handle),
      description: `Initials avatar: ${getInitialsFromHandle(handle)}`
    };
  }
  
  return {
    id: seedOrId,
    name: 'Pixel Avatar',
    url: getAvatarUrl(seedOrId, false),
    description: 'Pixel art avatar'
  };
};

// Generate random avatar for new users
export const getRandomAvatarOption = (): AvatarOption => {
  const randomSeed = `avatar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  return getAvatarOption(randomSeed, false);
};

// Get fallback avatar URL
export const getFallbackAvatarUrl = (): string => {
  return getAvatarUrl('fallback-user', false);
};